<script setup lang="ts">
    import Highcharts, { Options } from 'highcharts';
    import { computed } from 'vue';
    import { Chart } from 'highcharts-vue';
    import { IDonutChart } from '@/js/services/api/widget/types';
    import { __ } from '../../../stores/expressions';
    import BaseChart from './BaseChart.vue';

    const props = defineProps<{
        data: IDonutChart['donutChart'];
    }>();

    const mainSerieName: string = __('chart-main-serie');
    const backgroundName: string = __('chart-background-serie');

    const chartValue = computed(() => {
        if (props.data.value !== null) {
            return props.data.value.toFixed(1) + __('percentage-character');
        }

        return null;
    });

    const baseOptions = computed<Options>(() => ({
        chart: {
            type: 'pie',
            style: {
                fontFamily: 'inherit',
            },
            events: {
                render: function () {
                    const chart = this as Highcharts.Chart & {
                        customCenterLabel?: Highcharts.SVGElement;
                    };

                    if (chart.customCenterLabel) {
                        chart.customCenterLabel.destroy();
                    }

                    // 14% of the plot size. Minimum 10px and maximum 100px
                    const fontSize = Math.max(
                        10,
                        Math.min(100, Math.min(chart.plotWidth * 0.14, chart.plotHeight * 0.14))
                    );

                    chart.customCenterLabel = chart.renderer
                        .text(
                            chartValue.value || '',
                            chart.plotWidth / 2 + chart.plotLeft,
                            chart.plotHeight / 2 + chart.plotTop
                        )
                        .css({
                            fontSize: `${fontSize}px`,
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: '#490838',
                        })
                        .attr({
                            align: 'center',
                        })
                        .add();

                    const bbox = chart.customCenterLabel.getBBox();
                    chart.customCenterLabel.attr({
                        x: chart.plotLeft + chart.plotWidth / 2,
                        y: chart.plotTop + chart.plotHeight / 2 + bbox.height / 4,
                    });
                },
            },
        },
        title: { text: '' },
        tooltip: { enabled: false },
        legend: { enabled: false },
        exporting: { enabled: false },
        credits: { enabled: false },
        accessibility: { enabled: true },
        plotOptions: {
            pie: {
                borderRadius: 50,
                borderWidth: 0,
            },
            series: {
                dataLabels: {
                    enabled: false,
                },
                states: {
                    hover: {
                        enabled: true,
                        brightness: 0,
                        halo: {
                            size: 0,
                        },
                    },
                    inactive: {
                        enabled: false,
                    },
                },
            },
        },
        series: [
            {
                type: 'pie',
                name: '',
                innerSize: '70%',
                data: [{ name: mainSerieName, y: 100, color: 'var(--chart-background-color)' }],
            },
            {
                type: 'pie',
                name: mainSerieName,
                innerSize: '70%',
                data: [
                    {
                        name: mainSerieName,
                        y: props.data.value ?? 0,
                        color: 'var(--chart-color)',
                    },
                    {
                        name: backgroundName,
                        y: 100 - (props.data.value ?? 0),
                        color: 'var(--chart-background-color)',
                    },
                ],
            },
        ],
    }));
</script>

<template>
    <div
        class="Chart__container"
        style="
            --chart-color: var(--color-primary-plum);
            --chart-background-color: var(--color-primary-plum-200);
        "
    >
        <BaseChart :has-data="chartValue !== null">
            <Chart :options="baseOptions" :highcharts="Highcharts" />
        </BaseChart>
    </div>
</template>
